// @flow
import React, { Component, Fragment } from 'react';
import { defineMessages, FormattedMessage, intlShape } from 'react-intl';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';

import Checkbox from '../../../../../elements/Checkbox/Checkbox';
import { getLocalizedCountry } from '../../../../local-values/utils/countries';
import { Mobile, Default } from '../../../../../utils/responsive';
import ReportModalContainer from '../../../../report/container/ReportModalContainer';
import FloatingBottom from '../../../../../elements/FloatingBottom/FloatingBottom';

import commonStyle from '../../../components/OrderCommon.scss';
import boxStyle from '../../../../../assets/styles/box.scss';
import buttonStyle from '../../../../../assets/styles/button.scss';
import style from './Received.scss';

import type { Node } from 'react';
import type { Order } from '../../../../../entities';
import type { LocalValuesState } from '../../../../local-values/duck';
import type { Motive, MotiveKey } from '../../../../report/duck';
import type { LocaleState } from '../../../../locale/duck';

type Props = {
  +localValues: LocalValuesState;
  +order: Order;
  +isConfirmingArrival: boolean;
  +buildProducts: () => Node;
  +buildSummary: () => Node;
  +buildPurchaserInfo: () => Node;
  +confirmArrival: (orderId: string) => void;
  +motives?: { [key: MotiveKey]: Motive[] };
  +locale: LocaleState;
};

type State = {
  checked: boolean;
  reportModalVisible: boolean;
};

const messages = defineMessages({
  bottomMessage1: {
    id: 'order.sale.received.bottom.1',
  },
  bottomMessage2: {
    id: 'order.sale.received.bottom.2',
  },
});

export default class Received extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      checked: false,
      reportModalVisible: false,
    }
  }

  handleCheckboxChange = () => {
    this.setState({
      checked: !this.state.checked,
    });
  };

  handleCloseReportModal = () => {
    this.setState({ reportModalVisible: false });
  };

  handleOpenReportModal = () => {
    this.setState({ reportModalVisible: true });
  };

  render() {
    const {
      buildProducts, buildSummary, buildPurchaserInfo, order, isConfirmingArrival, confirmArrival, localValues, motives,
      locale,
    } = this.props;
    const { reportModalVisible } = this.state;
    const options = motives ? motives.traveler.map((motive: Motive) => ({
      value: motive.name,
      label: motive.translations[locale.locale.toUpperCase()],
    })) : [];
    return (<Fragment>
      <main className={commonStyle.mainWrap}>
        <section className={classNames(commonStyle.wrap, commonStyle.leftWrap)}>
          <section className={classNames(boxStyle.box, style.confirmation)}>
            <FormattedMessage id="order.sale.received.box.title">
              {txt => <h2 className={classNames(boxStyle.boxTitle, boxStyle.smallSpace, style.title)}>{txt}</h2>}
            </FormattedMessage>
            <FormattedMessage id="order.sale.received.confirmation">
              {txt =>
                <Checkbox color="orange"
                          value={'confirm-arrival'}
                          name={'confirm-arrival'}
                          isChecked={this.state.checked}
                          handleChange={this.handleCheckboxChange}
                          className={style.receivedCheckbox}>
                  {txt} {order.flight.arrivalAdmin1.name} -
                  {getLocalizedCountry(order.flight.arrivalCountry.code, this.context.intl.formatMessage, localValues.countriesMap)}
                </Checkbox>}
            </FormattedMessage>
          </section>
          <Mobile>
            <FormattedMessage id="order.sale.received.continue.button">{
              text => <button
                disabled={!this.state.checked || isConfirmingArrival}
                onClick={() => confirmArrival(order.id)}
                className={classNames(buttonStyle.btn, buttonStyle.orange, style.confirmArrival)}>{text}</button>
            }</FormattedMessage>
            <FormattedMessage id="order.sale.reserved.report.button">{
              txt => <button className={classNames(buttonStyle.btn, buttonStyle.redInverse, style.report)}
                             onClick={this.handleOpenReportModal}>{txt}</button>
            }</FormattedMessage>
          </Mobile>
          {buildProducts()}
        </section>
        <section className={classNames(commonStyle.wrap, commonStyle.rightWrap)}>
          {buildPurchaserInfo()}
          {buildSummary()}
          <Default>
            <FormattedMessage id="order.sale.received.continue.button">{
              text => <button
                disabled={!this.state.checked || isConfirmingArrival}
                onClick={() => confirmArrival(order.id)}
                className={classNames(buttonStyle.btn, buttonStyle.orange, style.confirmArrival)}>{text}</button>
            }</FormattedMessage>
            <FormattedMessage id="order.sale.received.report.button">{
              txt => <button className={classNames(buttonStyle.btn, buttonStyle.redInverse, style.report)}
                             onClick={this.handleOpenReportModal}>{txt}</button>
            }</FormattedMessage>
          </Default>
        </section>
        <section className={style.buttonsWrap}>
          <FloatingBottom offSetY={152}>
            <FormattedMessage id="order.sale.received.continue.button">{
              text => <button
                disabled={!this.state.checked || isConfirmingArrival}
                onClick={() => confirmArrival(order.id)}
                className={classNames(buttonStyle.btn, buttonStyle.orange)}>{text}</button>
            }</FormattedMessage>
            <FormattedMessage id="order.sale.received.report.button">{
              txt => <button className={classNames(buttonStyle.btn, buttonStyle.redInverse, style.below)}
                             onClick={this.handleOpenReportModal}>{txt}</button>
            }</FormattedMessage>
          </FloatingBottom>
        </section>
      </main>
      <hr className={style.line}/>
      <span className={style.bottomMessage}>
        {this.context.intl.formatMessage(messages.bottomMessage1)}
        {` ${order.flight.arrivalAdmin1.name} `}
        {this.context.intl.formatMessage(messages.bottomMessage2)}
      </span>
      <ReportModalContainer color="orange"
                            reportType="TRAVELER_TRACKING"
                            isOpen={reportModalVisible}
                            handleClose={this.handleCloseReportModal}
                            isDiy={order.isDiy}
                            id={order.id}
                            options={options}
                            successIntl="modal.report.traveler.success.text"/>
    </Fragment>);
  }
}

Received.propTypes = {
  localValues: PropTypes.object.isRequired,
  order: PropTypes.object.isRequired,
  isConfirmingArrival: PropTypes.bool.isRequired,
  buildProducts: PropTypes.func.isRequired,
  buildSummary: PropTypes.func.isRequired,
  buildPurchaserInfo: PropTypes.func.isRequired,
  confirmArrival: PropTypes.func.isRequired,
  motives: PropTypes.object,
  locale: PropTypes.object.isRequired,
};

Received.contextTypes = {
  intl: intlShape,
};



// WEBPACK FOOTER //
// ./src/modules/order/Sale/received/components/Received.jsx